/**
 * DC «Коммерция → Активность» — тотальный анализ логов клиентского приложения.
 * Источник: GET /api/commerce/activity/analytics — сырые сигналы по каждому юзеру.
 * «Теплоту» (готовность к покупке) считаем ТУТ из нормализованных сигналов + ползунки весов,
 * чтобы формула крутилась мгновенно. Вид — безрамочная сетка аватарок: размер и свечение по
 * теплоте, сортировка/линзы переставляют кружки. Детальный поповер — разбор метрик + действия
 * (личина / рассылка / оффер / копировать контакт).
 */
import { useCallback, useEffect, useMemo, useState, type CSSProperties, type ReactElement } from "react";
import { api } from "../../api/client";
import { useAvatarSrc } from "../../hooks/useAvatarSrc";
import { BroadcastPanel } from "./broadcast/BroadcastPanel"; /* 06.10: общая панель рассылки заданиями */

interface Person {
  tg: number; name: string; username: string; avatarUrl: string;
  hasPhone: number; clientId: string | null; isStaff: number;
  total: number; activeDays: number; firstSeen: string; lastSeen: string;
  appOpens: number; selfViews: number; viewActor: number; viewQuest: number; viewSpecial: number;
  fanJoin: number; tips: number; books: number; shopBuys: number; taps: number;
}
interface Scored extends Person { warmth: number; sub: { activity: number; intent: number; actor: number; spend: number; recency: number }; days: number; }
type Weights = { activity: number; intent: number; actor: number; spend: number; recency: number };
type SortKey = "warmth" | "returns" | "rewatch" | "intent" | "actor" | "spend" | "fresh";

const DEFAULT_W: Weights = { activity: 30, intent: 14, actor: 10, spend: 28, recency: 18 };

// Линзы: готовые «взгляды» на аудиторию — каждая задаёт веса, сортировку и фильтр.
const LENSES: { id: string; label: string; w: Weights; sort: SortKey; filter: (p: Person) => boolean }[] = [
  { id: "warm", label: "🔥 Тёплые", w: { activity: 30, intent: 14, actor: 10, spend: 28, recency: 18 }, sort: "warmth", filter: () => true },
  { id: "fan", label: "🎭 Фан-активные", w: { activity: 14, intent: 6, actor: 60, spend: 8, recency: 12 }, sort: "actor", filter: (p) => p.viewActor + p.fanJoin + p.tips > 0 },
  { id: "intent", label: "🛒 Интерес к покупке", w: { activity: 12, intent: 56, actor: 6, spend: 16, recency: 10 }, sort: "intent", filter: (p) => p.viewQuest + p.viewSpecial > 0 },
  { id: "nostalgic", label: "📼 Ностальгирующие", w: { activity: 42, intent: 8, actor: 10, spend: 10, recency: 30 }, sort: "rewatch", filter: (p) => p.selfViews >= 3 },
  { id: "new", label: "🌱 Новички", w: { activity: 20, intent: 14, actor: 8, spend: 20, recency: 38 }, sort: "fresh", filter: (p) => daysSince(p.firstSeen) <= 14 },
];

const SORT_LABELS: Record<SortKey, string> = {
  warmth: "Теплота", returns: "Возвраты", rewatch: "Пересмотры своих игр", intent: "Интерес к покупке",
  actor: "Привязка к актёрам", spend: "Оплаты", fresh: "Свежесть",
};
const SORTERS: Record<SortKey, (a: Scored, b: Scored) => number> = {
  warmth: (a, b) => b.warmth - a.warmth,
  returns: (a, b) => b.appOpens - a.appOpens,
  rewatch: (a, b) => b.selfViews - a.selfViews,
  intent: (a, b) => (b.viewQuest + b.viewSpecial) - (a.viewQuest + a.viewSpecial),
  actor: (a, b) => (b.viewActor + b.fanJoin + b.tips) - (a.viewActor + a.fanJoin + a.tips),
  spend: (a, b) => (b.books * 3 + b.shopBuys * 3 + b.tips) - (a.books * 3 + a.shopBuys * 3 + a.tips),
  fresh: (a, b) => new Date(b.firstSeen).getTime() - new Date(a.firstSeen).getTime(),
};
const WLABELS: { key: keyof Weights; label: string; hint: string }[] = [
  { key: "activity", label: "Активность/возвраты", hint: "заходит часто и пересматривает свои игры" },
  { key: "spend", label: "Оплаты/контакт", hint: "брони, лавка, чаевые, оставил телефон" },
  { key: "recency", label: "Свежесть", hint: "как недавно заходил (сегодня = максимум)" },
  { key: "intent", label: "Интерес к покупке", hint: "смотрит карточки квестов и спецверсий" },
  { key: "actor", label: "Привязка к актёрам", hint: "досье актёров, фан-клубы, чаевые актёрам" },
];

function daysSince(iso: string): number {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 9999;
  return Math.max(0, (Date.now() - t) / 86400000);
}
function fmtAgo(iso: string): string {
  const d = daysSince(iso);
  if (d < 1) return "сегодня";
  if (d < 2) return "вчера";
  return `${Math.floor(d)} дн. назад`;
}
function tierOf(w: number): "hot" | "warm" | "cool" { return w >= 0.6 ? "hot" : w >= 0.34 ? "warm" : "cool"; }
const TIER_COLOR = { hot: "#ff7a3c", warm: "#2de2e6", cool: "#6b727b" };

function score(people: Person[], w: Weights): Scored[] {
  const max = (sel: (p: Person) => number) => Math.max(1, ...people.map(sel));
  const mOpen = max((p) => p.appOpens), mDays = max((p) => p.activeDays), mSelf = max((p) => p.selfViews);
  const mActor = max((p) => p.viewActor), mFan = max((p) => p.fanJoin), mTips = max((p) => p.tips);
  const mQ = max((p) => p.viewQuest), mSp = max((p) => p.viewSpecial), mBook = max((p) => p.books), mShop = max((p) => p.shopBuys);
  const wsum = (w.activity + w.intent + w.actor + w.spend + w.recency) || 1;
  return people.map((p) => {
    const activity = 0.45 * (p.appOpens / mOpen) + 0.3 * (p.activeDays / mDays) + 0.25 * (p.selfViews / mSelf);
    const intent = 0.5 * (p.viewQuest / mQ) + 0.5 * (p.viewSpecial / mSp);
    const actor = 0.4 * (p.viewActor / mActor) + 0.4 * (p.fanJoin / mFan) + 0.2 * (p.tips / mTips);
    const spend = 0.45 * (p.books / mBook) + 0.2 * (p.shopBuys / mShop) + 0.2 * (p.tips / mTips) + 0.15 * (p.hasPhone ? 1 : 0);
    const days = daysSince(p.lastSeen);
    const recency = Math.pow(0.5, days / 7);
    const warmth = (w.activity * activity + w.intent * intent + w.actor * actor + w.spend * spend + w.recency * recency) / wsum;
    return { ...p, warmth, sub: { activity, intent, actor, spend, recency }, days };
  });
}

function AvatarImg({ url, name, size }: { url: string; name: string; size: number }): ReactElement {
  const { src, failed } = useAvatarSrc(url); // загрузка через глобальный семафор (см. useAvatarSrc)
  const ch = (name || "?").trim().charAt(0).toUpperCase() || "?";
  const hue = Math.abs([...(name || "?")].reduce((a, c) => (a * 31 + c.charCodeAt(0)) | 0, 7)) % 360;
  return (
    <span style={{ width: size, height: size, borderRadius: "50%", overflow: "hidden", display: "inline-grid", placeItems: "center", background: `hsl(${hue} 32% 26%)`, color: "#fff", fontSize: Math.round(size * 0.42), fontWeight: 800 }}>
      {src && !failed ? <img src={src} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : ch}
    </span>
  );
}

const box: CSSProperties = { border: "1px solid var(--surface-3)", borderRadius: 10, background: "var(--surface-1)" };
const chip = (on: boolean): CSSProperties => ({
  padding: "4px 9px", borderRadius: 999, fontSize: 12, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap",
  border: "1px solid " + (on ? "var(--role)" : "var(--surface-3)"), background: on ? "var(--role)" : "transparent", color: on ? "#06121a" : "var(--text-2)",
});

// Бизнес-дата (UTC+5) со сдвигом на N дней назад — конец периода по умолчанию = сегодня.
function bizDateOffset(daysAgo: number): string {
  return new Date(Date.now() + 5 * 3600000 - daysAgo * 86400000).toISOString().slice(0, 10);
}
// Сдвиг YYYY-MM-DD на N дней (конец периода − длина).
function shiftDate(iso: string, days: number): string {
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}
const fmtDm = (iso: string): string => (/^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}` : iso);

export function DcActivitySection({ onToast, onImpersonate, onCreateOffer, from: fromProp, to: toProp, onSetRange }: {
  onToast: (m: string) => void; onImpersonate?: (tg: number) => void; onCreateOffer?: (tg: number) => void;
  from?: string; to?: string; onSetRange?: (from: string, to: string) => void;
}): ReactElement {
  const [people, setPeople] = useState<Person[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [noStaff, setNoStaff] = useState(true);
  const [weights, setWeights] = useState<Weights>(DEFAULT_W);
  const [lensId, setLensId] = useState<string | null>("warm");
  const [sort, setSort] = useState<SortKey>("warmth");
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [openTg, setOpenTg] = useState<number | null>(null);
  const [castOpen, setCastOpen] = useState(false);
  // Период = диапазон из углового календаря (range-режим). Фолбэк — последний месяц.
  const to = toProp || bizDateOffset(0);
  const from = fromProp || shiftDate(to, -30);
  const spanDays = Math.max(1, Math.round((Date.parse(to) - Date.parse(from)) / 86400000));
  // Пресеты задают диапазон в угловом календаре (конец = сегодня, начало = сегодня − N).
  const applyPreset = (days: number): void => { const end = bizDateOffset(0); onSetRange?.(shiftDate(end, -days), end); };

  useEffect(() => {
    let live = true;
    setLoading(true); setErr(null);
    api<{ items: Person[] }>(`/api/commerce/activity/analytics?from=${from}&to=${to}${noStaff ? "" : "&staff=1"}`)
      .then((r) => { if (live) { setPeople(r.items ?? []); setLoading(false); } })
      .catch((e: unknown) => { if (live) { setErr(e instanceof Error ? e.message : "Ошибка"); setLoading(false); } });
    return () => { live = false; };
  }, [noStaff, from, to]);

  const scored = useMemo(() => score(people, weights), [people, weights]);
  const lens = LENSES.find((l) => l.id === lensId) || null;
  const shown = useMemo(() => {
    const base = lens ? scored.filter((p) => lens.filter(p)) : scored;
    return [...base].sort(SORTERS[sort]);
  }, [scored, lens, sort]);
  const hotCount = useMemo(() => scored.filter((p) => tierOf(p.warmth) === "hot").length, [scored]);
  const byTg = useMemo(() => new Map(scored.map((p) => [p.tg, p])), [scored]);

  const applyLens = (id: string) => {
    const l = LENSES.find((x) => x.id === id); if (!l) return;
    setLensId(id); setWeights(l.w); setSort(l.sort);
  };
  const setW = (k: keyof Weights, v: number) => { setWeights((w) => ({ ...w, [k]: v })); setLensId(null); };
  const toggleSel = (tg: number) => setSelected((s) => { const n = new Set(s); n.has(tg) ? n.delete(tg) : n.add(tg); return n; });
  const selectShownTop = (n: number) => { setSelectMode(true); setSelected(new Set(shown.slice(0, n).map((p) => p.tg))); };

  const copyContacts = useCallback(async (tgs: number[]) => {
    const lines = tgs.map((tg) => byTg.get(tg)).filter(Boolean).map((p) => (p!.username ? "@" + p!.username : "id" + p!.tg));
    try { await navigator.clipboard.writeText(lines.join("\n")); onToast(`Скопировано: ${lines.length}`); }
    catch { onToast("Не удалось скопировать"); }
  }, [byTg, onToast]);

  const detail = openTg != null ? byTg.get(openTg) || null : null;

  return (
    <section style={{ padding: 10, display: "flex", flexDirection: "column", gap: 10 }}>
      {/* Шапка: итог + линзы */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <strong style={{ fontSize: 14 }}>Активность</strong>
        <span style={{ fontSize: 12, color: "var(--text-3)" }}>{people.length} чел. · <span style={{ color: TIER_COLOR.hot, fontWeight: 700 }}>{hotCount} тёплых</span></span>
        <label style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, color: "var(--text-2)", cursor: "pointer" }}>
          <input type="checkbox" checked={noStaff} onChange={(e) => setNoStaff(e.target.checked)} /> без сотрудников
        </label>
      </div>
      {/* Период: длину задают пресеты, а КОНЕЦ берётся из стандартного календаря вверху (углового).
          Аналитика считает события только за [конец − длина .. конец]. Своих календарей тут нет. */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", fontSize: 12 }}>
        <span style={{ color: "var(--text-3)" }}>Период:</span>
        {([["Неделя", 7], ["Месяц", 30], ["3 мес", 90], ["Всё", 3650]] as [string, number][]).map(([lbl, d]) => (
          <button key={lbl} type="button" style={chip(spanDays === d)} onClick={() => applyPreset(d)}>{lbl}</button>
        ))}
        <span style={{ color: "var(--text-3)", marginLeft: 2 }}>{spanDays >= 3650 ? "за всё время" : `${fmtDm(from)} → ${fmtDm(to)}`}</span>
      </div>
      <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 2 }}>
        {LENSES.map((l) => <button key={l.id} type="button" style={chip(lensId === l.id)} onClick={() => applyLens(l.id)}>{l.label}</button>)}
        <button type="button" style={chip(lensId === null)} onClick={() => setLensId(null)} title="Показать всех, крутить веса вручную">⚙ Свой</button>
      </div>

      <div style={{ fontSize: 10.5, color: "var(--text-3)", lineHeight: 1.35 }}>
        🌡 Число под аватаром — «температура» 0–100: насколько человек «тёплый» / готов к покупке по формуле ниже. Крути ползунки — сетка пересортируется.
      </div>

      {/* Сортировка + ползунки весов */}
      <div style={{ ...box, padding: 9, display: "flex", flexDirection: "column", gap: 9 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, color: "var(--text-3)" }}>Сортировка</span>
          <select value={sort} onChange={(e) => setSort(e.target.value as SortKey)}
            style={{ padding: "4px 8px", borderRadius: 8, fontSize: 12, background: "var(--surface-2)", color: "var(--text-1)", border: "1px solid var(--surface-3)" }}>
            {(Object.keys(SORT_LABELS) as SortKey[]).map((k) => <option key={k} value={k}>{SORT_LABELS[k]}</option>)}
          </select>
          <button type="button" style={{ ...chip(selectMode), marginLeft: "auto" }} onClick={() => { setSelectMode((v) => !v); if (selectMode) setSelected(new Set()); }}>
            {selectMode ? "✓ Выделение" : "Выделять"}
          </button>
          {selectMode ? <button type="button" style={chip(false)} onClick={() => selectShownTop(20)}>Топ-20</button> : null}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
          {WLABELS.map(({ key, label, hint }) => (
            <div key={key} style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0 }}>
              <span style={{ flex: "0 0 150px", minWidth: 0, lineHeight: 1.15 }} title={hint}>
                <span style={{ fontSize: 11.5, fontWeight: 700, color: "var(--text-1)" }}>{label}</span>
                <span style={{ display: "block", fontSize: 9.5, color: "var(--text-3)" }}>{hint}</span>
              </span>
              <input type="range" min={0} max={100} value={weights[key]} onChange={(e) => setW(key, Number(e.target.value))} style={{ flex: 1, minWidth: 40, accentColor: "var(--role)" }} />
              <span style={{ flex: "0 0 26px", textAlign: "right", fontFamily: "var(--f-mono, monospace)", fontSize: 12, color: "var(--text-3)" }}>{weights[key]}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Панель выделения */}
      {selectMode && selected.size > 0 ? (
        <div style={{ ...box, padding: "7px 10px", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", position: "sticky", top: 0, zIndex: 4, borderColor: "var(--role)" }}>
          <strong style={{ fontSize: 13 }}>Выбрано: {selected.size}</strong>
          <button type="button" style={{ ...chip(true), marginLeft: "auto" }} onClick={() => setCastOpen(true)}>→ Рассылка</button>
          <button type="button" style={chip(false)} onClick={() => copyContacts([...selected])}>Копировать контакты</button>
          <button type="button" style={chip(false)} onClick={() => setSelected(new Set())}>Снять</button>
        </div>
      ) : null}

      {/* Сетка аватарок */}
      {loading ? <div style={{ padding: 30, textAlign: "center", color: "var(--text-3)" }}>Считаю активность…</div>
        : err ? <div style={{ padding: 20, textAlign: "center", color: "#ff6b6b" }}>{err}</div>
        : shown.length === 0 ? <div style={{ padding: 30, textAlign: "center", color: "var(--text-3)" }}>Под эту линзу никто не подходит.</div>
        : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "10px 8px", justifyContent: "center", alignItems: "flex-start", paddingTop: 4 }}>
            {shown.map((p) => {
              const t = tierOf(p.warmth);
              const size = Math.round(46 + p.warmth * 10); // мягкая разница: 46–56px
              const sel = selected.has(p.tg);
              const temp = Math.round(p.warmth * 100);
              const glow = sel ? "0 0 0 3px var(--role)"
                : t === "hot" ? `0 0 0 2px ${TIER_COLOR.hot}, 0 0 12px ${TIER_COLOR.hot}66`
                : t === "warm" ? `0 0 0 2px ${TIER_COLOR.warm}aa` : "0 0 0 1px var(--surface-3)";
              return (
                <div key={p.tg} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3, width: 62 }}>
                  <button type="button" title={p.name || (p.username ? "@" + p.username : String(p.tg))}
                    onClick={() => (selectMode ? toggleSel(p.tg) : setOpenTg(p.tg))}
                    style={{ position: "relative", padding: 0, border: "none", background: "transparent", cursor: "pointer", lineHeight: 0, opacity: 0.82 + p.warmth * 0.18, borderRadius: "50%", boxShadow: glow }}>
                    <AvatarImg url={p.avatarUrl} name={p.name || p.username} size={size} />
                    {sel ? <span style={{ position: "absolute", left: -2, top: -2, width: 15, height: 15, borderRadius: "50%", background: "var(--role)", color: "#06121a", fontSize: 10, fontWeight: 900, display: "grid", placeItems: "center", border: "1.5px solid var(--surface-1)" }}>✓</span> : null}
                  </button>
                  <span style={{ fontSize: 10.5, fontWeight: 800, fontFamily: "var(--f-mono, monospace)", color: TIER_COLOR[t], lineHeight: 1 }} title="температура (0–100)">{temp}°</span>
                </div>
              );
            })}
          </div>
        )}

      {detail ? <DetailModal p={detail} onClose={() => setOpenTg(null)}
        selected={selected.has(detail.tg)} onToggleSel={() => { setSelectMode(true); toggleSel(detail.tg); }}
        onImpersonate={onImpersonate} onCreateOffer={onCreateOffer} onCopy={() => copyContacts([detail.tg])} /> : null}
      {castOpen ? <BroadcastPanel selectedIds={[...selected]} title="Рассылка тёплым" onClose={() => setCastOpen(false)} onSent={() => { setCastOpen(false); setSelected(new Set()); }} onToast={onToast} /> : null}
    </section>
  );
}

function Metric({ label, value, accent }: { label: string; value: number | string; accent?: boolean }): ReactElement {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 12.5, padding: "3px 0", borderBottom: "1px solid var(--surface-2)" }}>
      <span style={{ color: "var(--text-3)" }}>{label}</span>
      <span style={{ fontWeight: 700, color: accent ? "var(--role)" : "var(--text-1)", fontFamily: "var(--f-mono, monospace)" }}>{value}</span>
    </div>
  );
}

function DetailModal({ p, onClose, selected, onToggleSel, onImpersonate, onCreateOffer, onCopy }: {
  p: Scored; onClose: () => void; selected: boolean; onToggleSel: () => void;
  onImpersonate?: (tg: number) => void; onCreateOffer?: (tg: number) => void; onCopy: () => void;
}): ReactElement {
  const t = tierOf(p.warmth);
  const tierLabel = t === "hot" ? "Тёплый, готов к разговору" : t === "warm" ? "Прогревается" : "Холодный";
  const act = (label: string, fn: (() => void) | undefined, primary?: boolean) => (
    <button type="button" disabled={!fn} onClick={fn} style={{
      flex: "1 1 45%", padding: "8px 6px", borderRadius: 9, fontSize: 12.5, fontWeight: 700, cursor: fn ? "pointer" : "not-allowed",
      border: "1px solid " + (primary ? "var(--role)" : "var(--surface-3)"), background: primary ? "var(--role)" : "var(--surface-2)",
      color: primary ? "#06121a" : "var(--text-1)", opacity: fn ? 1 : 0.5,
    }}>{label}</button>
  );
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 50, background: "rgba(0,0,0,.6)", display: "grid", placeItems: "center", padding: 14 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ ...box, width: "min(420px, 96vw)", maxHeight: "88vh", overflowY: "auto", padding: 14, background: "var(--surface-1)", borderColor: TIER_COLOR[t] }}>
        <div style={{ display: "flex", gap: 11, alignItems: "center" }}>
          <span style={{ boxShadow: `0 0 0 2px ${TIER_COLOR[t]}88`, borderRadius: "50%", lineHeight: 0 }}><AvatarImg url={p.avatarUrl} name={p.name || p.username} size={52} /></span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 800, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.name || (p.username ? "@" + p.username : "id" + p.tg)}</div>
            <div style={{ fontSize: 12, color: "var(--text-3)" }}>{p.username ? "@" + p.username : "id" + p.tg}{p.hasPhone ? " · 📱 телефон есть" : ""}</div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 26, fontWeight: 900, color: TIER_COLOR[t], lineHeight: 1 }}>{Math.round(p.warmth * 100)}</div>
            <div style={{ fontSize: 9, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: 0.5 }}>тепло</div>
          </div>
        </div>
        <div style={{ fontSize: 12, color: TIER_COLOR[t], fontWeight: 700, margin: "8px 0 4px" }}>{tierLabel}</div>
        <div style={{ margin: "6px 0" }}>
          <Metric label="Возвраты в приложение" value={p.appOpens} accent />
          <Metric label="Пересмотры своих игр" value={p.selfViews} accent />
          <Metric label="Активных дней" value={p.activeDays} />
          <Metric label="Досье актёров" value={p.viewActor} />
          <Metric label="Квесты / спецверсии" value={`${p.viewQuest} / ${p.viewSpecial}`} />
          <Metric label="Фан-клубы · чаевые" value={`${p.fanJoin} · ${p.tips}`} />
          <Metric label="Брони · лавка" value={`${p.books} · ${p.shopBuys}`} accent={p.books + p.shopBuys > 0} />
          <Metric label="Последний визит" value={fmtAgo(p.lastSeen)} />
        </div>
        <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginTop: 10 }}>
          {act("👁 Личина", onImpersonate ? () => onImpersonate(p.tg) : undefined)}
          {act(selected ? "✓ В рассылке" : "✉ В рассылку", onToggleSel, selected)}
          {act("🎁 Оффер", onCreateOffer ? () => onCreateOffer(p.tg) : undefined)}
          {act("📋 Контакт", onCopy)}
        </div>
        <button type="button" onClick={onClose} style={{ width: "100%", marginTop: 9, padding: "7px", borderRadius: 9, fontSize: 12.5, background: "transparent", color: "var(--text-3)", border: "1px solid var(--surface-3)", cursor: "pointer" }}>Закрыть</button>
      </div>
    </div>
  );
}
